import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { ServiceProviderOpticDropdown } from './service-provider-optic-dropdown';
import { useServiceProviderCitizenContext } from '../../citizen-context-hooks';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { useFeatureToggles } from '../../../../feature-toggles/feature-toggles';
import { SocialProviderUnitDto } from '../../../../api-service/clients';
import {
  AssignmentCountsByUnitId,
  compareUnitsByAssignmentCounts,
  formatUnitWithAssignmentCounts,
  toAssignmentCountsByUnitId,
} from './service-provider-unit-assignment-counts';

export const serviceProviderOpticAssignmentCountsToggle =
  'ServiceProviderOpticAssignmentCounts';

// The counts are an aid for picking a unit, so a failed lookup should not show an error.
const assignmentClient =
  moduleClientFactory.createCitizenAssignmentClient(false);

export function ServiceProviderOpticSelectContainer() {
  const {
    citizenId,
    currentServiceProvider,
    setCurrentServiceProvider,
    serviceProviderUnits,
  } = useServiceProviderCitizenContext();
  const { isFeatureEnabled } = useFeatureToggles();
  const showAssignmentCounts = isFeatureEnabled(
    serviceProviderOpticAssignmentCountsToggle
  );
  const [assignmentCounts, setAssignmentCounts] =
    useState<AssignmentCountsByUnitId>(new Map());

  useEffect(() => {
    if (!showAssignmentCounts || !citizenId) {
      return;
    }

    let isCurrent = true;
    assignmentClient
      .getAssignmentCountsPerServiceProviderUnit({ citizenId })
      .then(response => {
        if (isCurrent) {
          setAssignmentCounts(
            toAssignmentCountsByUnitId(
              response.serviceProviderUnitAssignmentCounts ?? []
            )
          );
        }
      })
      .catch(() => {
        // The optic falls back to the plain alphabetical list.
      });

    return () => {
      isCurrent = false;
    };
  }, [showAssignmentCounts, citizenId]);

  if (!serviceProviderUnits || serviceProviderUnits.length === 0) {
    return null;
  }

  const selectedUnit = serviceProviderUnits.find(
    u => u.id === currentServiceProvider?.id
  );

  function getUnitLabel(unit: SocialProviderUnitDto) {
    return formatUnitWithAssignmentCounts(unit, assignmentCounts);
  }

  function sortUnits(a: SocialProviderUnitDto, b: SocialProviderUnitDto) {
    return compareUnitsByAssignmentCounts(a, b, assignmentCounts);
  }

  return (
    <Box sx={{ ml: 1 }}>
      <ServiceProviderOpticDropdown
        units={serviceProviderUnits ?? []}
        selectedUnitId={currentServiceProvider?.id}
        onSelectUnit={setCurrentServiceProvider}
        selectedUnitAbbreviation={selectedUnit?.abbreviation}
        getUnitLabel={showAssignmentCounts ? getUnitLabel : undefined}
        sortUnits={showAssignmentCounts ? sortUnits : undefined}
      />
    </Box>
  );
}
